/**
 * /d/:token — a read-only WeKIT™ Career Clarity 360 Passport opened from a shared link.
 *
 * No auth, no engine run, no store. The snapshot was frozen when the learner
 * pressed share, so a parent or teacher reads exactly the pages the learner
 * saw, and there is nothing here they can change.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Printer, Download, Lock, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { DossierBody } from "@/features/assessment-v2/components/DossierSections";
import { downloadDossierPdf, dossierFileName } from "@/features/assessment-v2/downloadDossierPdf";
import type { Dossier } from "@/features/assessment-v2/dossier";
import { toast } from "@/hooks/use-toast";
import "@/features/assessment-v2/dossier-print.css";

export default function SharedDossier() {
  const { token } = useParams<{ token: string }>();
  const [dossier, setDossier] = useState<Dossier | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error: fnError } = await supabase.functions.invoke(
          `public-dossier?token=${encodeURIComponent(token ?? "")}`,
          { method: "GET" },
        );
        if (cancelled) return;
        if (fnError) throw fnError;
        if (data?.error) throw new Error(data.error);
        setDossier(data.dossier as Dossier);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "This report couldn't be opened.");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const download = async () => {
    if (!dossier) return;
    setDownloading(true);
    try {
      await downloadDossierPdf(dossierFileName(dossier.learnerName));
    } catch (e) {
      toast({
        title: "Couldn't build the PDF",
        description: e instanceof Error ? e.message : "Use Print instead — it saves as PDF too.",
        variant: "destructive",
      });
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    if (dossier) document.title = `${dossier.learnerName} · WeKIT™ Career Clarity 360 Passport | WeKIT`;
  }, [dossier]);

  if (error) {
    return (
      <div className="min-h-dvh bg-background text-foreground grid place-items-center px-6">
        <div className="max-w-md text-center space-y-4">
          <h1 className="font-display text-2xl">This report isn't available.</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
          <Link
            to="/"
            className="inline-flex min-h-[48px] items-center px-6 rounded-2xl bg-primary text-primary-foreground"
          >
            Go to WeKIT
          </Link>
        </div>
      </div>
    );
  }

  if (!dossier) {
    return (
      <div className="min-h-dvh bg-background text-foreground grid place-items-center px-6">
        <p className="text-sm text-muted-foreground animate-pulse">Opening this report…</p>
      </div>
    );
  }

  return (
    <div className="dossier-root min-h-dvh bg-background text-foreground">
      <div className="no-print sticky top-0 z-20 border-b border-border bg-background/85 backdrop-blur">
        <div className="mx-auto max-w-3xl px-6 h-16 flex items-center justify-between gap-4">
          <p className="inline-flex items-center gap-2 text-xs text-muted-foreground">
            <Lock className="h-3.5 w-3.5" aria-hidden="true" />
            Read-only · shared by {dossier.learnerName}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={download}
              disabled={downloading}
              className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-2xl border border-border
                text-sm hover:bg-muted disabled:opacity-60
                focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {downloading
                ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                : <Download className="h-4 w-4" aria-hidden="true" />}
              PDF
            </button>
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-2xl bg-primary
                text-primary-foreground text-sm font-medium hover:opacity-90
                focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <Printer className="h-4 w-4" aria-hidden="true" /> Print
            </button>
          </div>
        </div>
      </div>

      <main>
        <DossierBody d={dossier} />
      </main>
    </div>
  );
}
